import { useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Karta } from '@/components/Karta';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { PasSekcji } from '@/components/PasSekcji';
import { Segmenty } from '@/components/Segmenty';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { MetricBasisChips } from '@/components/MetricBasisChips';
import { MiniMetryka, PlytaGlowna } from '@/components/PlytaGlowna';
import { PrzyciskDowodu, type Dowod } from '@/components/SzufladaDowodu';
import { formatCount, formatGrosze } from '@/lib/format';
import { czyPustyZbior, wartoscDoPokazania } from '@/lib/pustyZbior';
import { useTransactionDetail, useTransactions } from '@/lib/useAnalytics';
import type { TransactionDetail, TransactionListRow } from '@/lib/api/analytics';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * O1 — REJESTR POJEDYNCZEJ PŁATNOŚCI. Migracja do języka „SYGNAŁ" (kierunek C, R5-15-009).
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * PYTANIE EKRANU: co dokładnie zawierała TA płatność i ile naprawdę za nią pobrano.
 * To jedyna zdolność administracyjna, której brak blokuje wyłączenie starego panelu — analityka
 * zbiorcza na pytanie reklamacyjne nie odpowiada i nigdy nie odpowie.
 *
 * DWIE KWOTY SĄ TU SEDNEM, NIE SZCZEGÓŁEM.
 *   WARTOŚĆ WG CENNIKA  — ceny katalogowe pozycji, przed zniżką szkolną (crm_transactions.amount_grosze)
 *   FAKTYCZNIE POBRANO  — pieniądze, które wpłynęły (crm_transactions.payed_amount_grosze)
 * Dostają OSOBNE BLOKI o różnym wyglądzie, a nie dwa sąsiednie wiersze tabeli. Dwie liczby
 * postawione w jednej kolumnie czyta się jak dwie wersje tej samej wielkości — a to są dwie
 * różne metryki (ADR-001) i przy reklamacji mylenie ich jest całą sprawą.
 *
 * RÓŻNICĘ liczymy WYŁĄCZNIE gdy znamy obie kwoty. Przy nieznanej kwocie pobranej różnica
 * wyszłaby równa pełnej wartości katalogowej i wyglądała jak stuprocentowy rabat — czyli
 * poprawnie wyliczone kłamstwo. Brak kwoty pobranej to „brak danych", NIGDY 0,00 zł.
 *
 * JEDNA PŁYTA GŁÓWNA — i tylko wtedy, gdy ekran ma JEDNĄ liczbę-bohatera. W widoku rejestru
 * jest nią liczba płatności, które w ogóle widać. W widoku szczegółu płyty NIE MA celowo:
 * musiałaby wybrać jedną z dwóch równorzędnych kwot i tym samym podporządkować drugą — czyli
 * zrobić dokładnie to, czego ten ekran ma nie robić.
 *
 * PRYWATNOŚĆ. Bez imienia, nazwiska, e-maila i telefonu — do odnalezienia płatności wystarcza
 * identyfikator kupującego. Stary panel pokazuje dane osobowe; to nie jest powód, żeby je tu
 * przenosić.
 *
 * ZERO DANYCH DEMONSTRACYJNYCH. Ekran czyta wyłącznie `/api/crm/transactions` przez istniejące
 * haki. Gdy rejestr jest pusty, pokazujemy pustkę i nazywamy JEJ RODZAJ.
 */

/**
 * Wartość „bez zawężenia" w przełączniku statusu. Osobna stała, bo pusty łańcuch bywa poprawnym
 * statusem w cudzym rejestrze i nie wolno go zjeść jako „wszystkie".
 */
const WSZYSTKIE = '__wszystkie__';

/** Jedno źródło prawdy o nazwie źródła — powtarzane w płycie i w dowodach. */
const ZRODLO_LISTA = 'CRM /api/crm/transactions → crm_transactions (+ crm_schools, crm_restaurants)';
const zrodloSzczegolu = (id: number) => `CRM /api/crm/transactions/${id} → crm_transactions`;

/**
 * Świeżość źródła sprzedaży jest w całym programie NIEUSTALONA. Wpisanie tu stempla odczytu
 * byłoby dokładnie tym błędem, który ekran „Jakość danych" nazywa największą otwartą luką.
 */
const SWIEZOSC = 'NIEUSTALONA — źródło nie raportuje momentu odczytu';

/**
 * Filtr sprzedaży stosowany w ANALITYCE (crm/backend/routes/read.js). Rejestr go NIE stosuje —
 * i to jest powód, dla którego liczba wierszy tutaj nie musi zgadzać się z liczbą zamówień
 * na ekranach sprzedaży.
 */
const DEFINICJA_SPRZEDAZY = 'transaction_type ∈ {0, 2} · status = 1';

/**
 * Znaczenie kodów typu wg definicji z backendu CRM (routes/read.js — sprzedaż {0,2},
 * doładowania {1,3}; routes/customer-profile.js — 3 = doładowanie administratora).
 * Kodu spoza tej listy NIE nazywamy: zgadnięta etykieta brzmi wiarygodnie i przez to jest
 * gorsza niż jawne „znaczenie nieudokumentowane".
 */
const TYP_TRANSAKCJI: Record<number, string> = {
  0: 'zamówienie',
  1: 'doładowanie portfela (wpłata klienta)',
  2: 'zamówienie',
  3: 'doładowanie portfela (operacja administratora)',
};

function opisTypu(typ: number | null): string {
  if (typ === null) return '—';
  return `${typ} — ${TYP_TRANSAKCJI[typ] ?? 'znaczenie kodu nieudokumentowane'}`;
}

/** Kwota z jawnym rozróżnieniem BRAKU od ZERA. Null nigdy nie zamienia się w 0,00 zł. */
function Kwota({ grosze, brakLabel = 'brak danych' }: { grosze: number | null; brakLabel?: string }) {
  if (grosze === null || grosze === undefined) {
    return (
      <Typography component="span" variant="body2" sx={{ color: 'var(--tekst-slaby)', fontStyle: 'italic' }}>
        {brakLabel}
      </Typography>
    );
  }
  return (
    <Typography component="span" variant="body2" className="sygnal-liczba" sx={{ fontWeight: 600 }}>
      {formatGrosze(grosze)}
    </Typography>
  );
}

/**
 * BLOK KWOTY — jedna z dwóch kwot transakcji, w formacie „liczba jako grafika".
 *
 * Wariant ciemny dostaje kwota FAKTYCZNIE POBRANA. To nie jest ozdoba: gdy obie kwoty wyglądają
 * identycznie, wzrok traktuje je jak jedną wielkość w dwóch kolumnach. Różny materiał bloku
 * mówi „to są dwie różne rzeczy" szybciej niż jakikolwiek podpis.
 */
function BlokKwoty({
  etykieta,
  grosze,
  znaczenie,
  chipy,
  dowod,
  ciemny = false,
  kartaId,
}: {
  etykieta: string;
  grosze: number | null;
  znaczenie: string;
  chipy: ReactNode;
  dowod: Dowod;
  ciemny?: boolean;
  /** Stabilny adres karty dla walidacji DOM (R5-15) — wyłącznie identyfikator, bez wpływu na wygląd. */
  kartaId?: string;
}) {
  return (
    <Paper
      variant="outlined"
      data-cc-card-id={kartaId}
      sx={{
        p: 'var(--o4)',
        borderRadius: 'var(--r-karta)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--o2)',
        borderColor: ciemny ? 'var(--linia-na-ciemnym)' : 'var(--linia)',
        background: ciemny
          ? 'linear-gradient(135deg, var(--atrament-3) 0%, var(--atrament) 72%)'
          : 'var(--powierzchnia)',
        color: ciemny ? 'var(--tekst-na-ciemnym)' : 'var(--tekst)',
        boxShadow: ciemny ? 'var(--cien-2)' : 'var(--cien-1)',
      }}
    >
      <Typography
        variant="overline"
        sx={{ color: ciemny ? 'var(--tekst-na-ciemnym)' : 'var(--tekst-slaby)', opacity: ciemny ? 0.62 : 1 }}
      >
        {etykieta}
      </Typography>

      <Typography
        className="sygnal-liczba"
        sx={{
          fontFamily: 'var(--krój-display)',
          fontSize: 'clamp(28px, 4.4vw, 48px)',
          lineHeight: 1,
          wordBreak: 'break-word',
          color: ciemny ? 'var(--zloto)' : 'var(--tekst)',
        }}
      >
        {/* Brak NIE jest zerem. Zero to zmierzony wynik, brak to niewiedza. */}
        {grosze === null ? (
          <Box component="span" sx={{ fontSize: '0.46em', fontStyle: 'italic', opacity: 0.72 }}>
            brak danych
          </Box>
        ) : (
          formatGrosze(grosze)
        )}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: ciemny ? 'var(--tekst-na-ciemnym)' : 'var(--tekst-2)',
          opacity: ciemny ? 0.78 : 1,
          maxWidth: '46ch',
        }}
      >
        {znaczenie}
      </Typography>

      <Box sx={{ mt: 'auto', pt: 'var(--o2)' }}>{chipy}</Box>

      <Box
        sx={{
          ml: '-10px',
          '& .MuiButton-root': ciemny ? { color: 'var(--zloto)' } : undefined,
        }}
      >
        <PrzyciskDowodu dowod={dowod} etykieta="Skąd ta kwota" />
      </Box>
    </Paper>
  );
}

function Pole({ etykieta, wartosc, podpis }: { etykieta: string; wartosc: string; podpis?: string }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: 'var(--tekst-slaby)',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          fontWeight: 700,
        }}
      >
        {etykieta}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
        {wartosc}
      </Typography>
      {podpis ? (
        <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block' }}>
          {podpis}
        </Typography>
      ) : null}
    </Box>
  );
}

/** Szczegół jednej płatności — ekran obsługi reklamacji we właściwym znaczeniu. */
function Szczegol({ id, onZamknij }: { id: number; onZamknij: () => void }) {
  const { data, isLoading, error } = useTransactionDetail(id);

  if (isLoading) return <LoadingState label="Wczytywanie transakcji…" />;
  if (error) {
    return (
      <ErrorState
        error={error}
        context="Control Center czyta wyłącznie API. Gdy rejestr nie odpowiada, nie pokazujemy kwot zastępczych."
      />
    );
  }
  if (!data) {
    return (
      <EmptyState
        title="Nie znaleziono transakcji"
        description="Rejestr nie zwrócił wiersza o tym numerze. To nie dowodzi, że płatność nie istniała — dowodzi, że nie ma jej w tym źródle."
      />
    );
  }

  return <SzczegolTresc data={data} onZamknij={onZamknij} />;
}

function SzczegolTresc({ data, onZamknij }: { data: TransactionDetail; onZamknij: () => void }) {
  const id = data.transaction_id;
  const wartoscKatalogowa = data.list_value_total_grosze;
  const kwotaPobrana = data.paid_amount_grosze;
  const roznica = data.discount_difference_grosze;

  /*
   * Suma wartości pozycji liczona TYLKO wtedy, gdy każda pozycja ma wartość. Suma po zbiorze
   * z dziurami wygląda jak pełna, a jest niepełna — i właśnie taka liczba trafiłaby potem
   * do rozmowy z klientem.
   */
  const wartosciLinii = data.order_items.map((i) => i.line_list_value_grosze);
  const sumaZnana = wartosciLinii.length > 0 && wartosciLinii.every((v) => v !== null);
  const sumaPozycji = sumaZnana ? wartosciLinii.reduce<number>((a, v) => a + (v ?? 0), 0) : null;
  const zgodnaSuma =
    sumaPozycji === null || wartoscKatalogowa === null ? null : sumaPozycji === wartoscKatalogowa;
  const usunietePozycje = data.order_items.filter((i) => i.is_deleted).length;
  const brakIlosci = data.order_items.filter((i) => i.quantity === null).length;

  const oknoRekordu = `pojedynczy rekord — transakcja #${id}, dzień zakupu ${data.order_date ?? 'nieznany'}`;

  /*
   * Dowody budowane z TYCH SAMYCH pól, które zasilają liczby wyżej. Warstwa prezentacji nie może
   * być właścicielem liczb — dwa niezależnie pisane opisy tej samej wielkości rozjeżdżają się
   * przy pierwszej zmianie definicji i wtedy produkt kłamie w jednym z dwóch miejsc.
   */
  const dowodWartosciKatalogowej: Dowod = {
    tytul: `Wartość wg cennika — transakcja #${id}`,
    etykietaWartosci: 'Wartość wg cennika',
    wartosc: wartoscKatalogowa === null ? null : formatGrosze(wartoscKatalogowa),
    podstawa: { value: 'LIST_VALUE', time: 'ORDER_DATE' },
    zrodlo: `${zrodloSzczegolu(id)}.amount_grosze`,
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: SWIEZOSC,
    oknoBiezace: oknoRekordu,
    oknoPorownawcze: null,
    wyliczenie:
      'Wartość pola amount_grosze wiersza transakcji — kwota zapisana przy zakupie w cenach ' +
      'katalogowych. To NIE jest suma pozycji pokazanych niżej: pozycje pobierane są po numerze ' +
      'ZAMÓWIENIA, a transakcja może rozliczać jego część.',
    szczegoly: [
      ['Pole źródłowe', 'crm_transactions.amount_grosze'],
      ['Wartość w źródle', wartoscKatalogowa === null ? 'NULL (brak)' : formatGrosze(wartoscKatalogowa)],
      ['Suma wartości pozycji', sumaPozycji === null ? 'nie do policzenia' : formatGrosze(sumaPozycji)],
      [
        'Suma pozycji = wartość transakcji',
        zgodnaSuma === null ? 'nie da się sprawdzić' : zgodnaSuma ? 'tak' : 'NIE',
      ],
    ],
    ograniczenia: [
      'Ceny katalogowe są sprzed zniżki szkolnej — szkoły z promocją zapłaciły mniej.',
      'Świeżość źródła nieustalona: liczba jest poprawna dla momentu odczytu, ale ten moment jest nieznany.',
    ],
    czegoNieWynika:
      'To NIE jest przychód i nie mówi, ile uczeń zapłacił — od tej kwoty odpowiada wyłącznie pole ' +
      '„faktycznie pobrano". Nie wynika z niej również, że kupujący dostał wszystkie pozycje z listy ' +
      'poniżej ani że rozliczył je tą jedną transakcją.',
  };

  const dowodKwotyPobranej: Dowod = {
    tytul: `Faktycznie pobrano — transakcja #${id}`,
    etykietaWartosci: 'Kwota pobrana',
    wartosc: kwotaPobrana === null ? null : formatGrosze(kwotaPobrana),
    podstawa: { value: 'PAID_AMOUNT', time: 'ORDER_DATE' },
    zrodlo: `${zrodloSzczegolu(id)}.payed_amount_grosze`,
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: SWIEZOSC,
    oknoBiezace: oknoRekordu,
    oknoPorownawcze: null,
    wyliczenie:
      'Wartość pola payed_amount_grosze wiersza transakcji, bez żadnego przeliczenia. NULL znaczy ' +
      '„źródło nie niesie tej wartości" — pokazujemy wtedy „brak danych" i nie podstawiamy zera.',
    szczegoly: [
      ['Pole źródłowe', 'crm_transactions.payed_amount_grosze'],
      ['Wartość w źródle', kwotaPobrana === null ? 'NULL (brak)' : formatGrosze(kwotaPobrana)],
      ['Braki zgłoszone przez źródło', data.missing_fields.length ? data.missing_fields.join(', ') : 'brak'],
      ['Status wiersza', String(data.status ?? 'brak')],
      ['Typ transakcji', opisTypu(data.transaction_type)],
    ],
    ograniczenia: [
      'Kwota pobrana istnieje WYŁĄCZNIE na ziarnie transakcji — nie da się jej przypisać do dania.',
      'Rejestr nie stosuje filtra sprzedaży z analityki, więc w tym samym zbiorze bywają doładowania portfela.',
    ],
    czegoNieWynika:
      'Nie wynika z tego, czy pieniądze zostały później zwrócone, ani czy to CAŁOŚĆ zapłaty za ' +
      'zamówienie — inne transakcje tego samego zamówienia nie są tu sumowane. Brak wartości nie ' +
      'znaczy, że nie pobrano nic; znaczy, że nie wiemy.',
  };

  const dowodRoznicy: Dowod = {
    tytul: `Różnica kwot — transakcja #${id}`,
    etykietaWartosci: 'Wartość wg cennika − faktycznie pobrano',
    wartosc: roznica === null ? null : formatGrosze(roznica),
    podstawa: { time: 'ORDER_DATE' },
    zrodlo: zrodloSzczegolu(id),
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: SWIEZOSC,
    oknoBiezace: oknoRekordu,
    oknoPorownawcze: null,
    wyliczenie:
      'amount_grosze − payed_amount_grosze, liczone po stronie backendu WYŁĄCZNIE gdy obie kwoty ' +
      'są znane. Przy nieznanej kwocie pobranej różnica wyszłaby równa pełnej wartości katalogowej ' +
      'i wyglądałaby jak stuprocentowy rabat — dlatego wtedy nie liczymy jej wcale.',
    szczegoly: [
      ['Wartość wg cennika', wartoscKatalogowa === null ? 'NULL (brak)' : formatGrosze(wartoscKatalogowa)],
      ['Faktycznie pobrano', kwotaPobrana === null ? 'NULL (brak)' : formatGrosze(kwotaPobrana)],
      ['Policzalna', roznica === null ? 'NIE — brakuje co najmniej jednej kwoty' : 'tak'],
    ],
    czegoNieWynika:
      'To NIE jest „udzielona zniżka" ani kwota do zwrotu przy reklamacji. Różnica dwóch metryk ' +
      'może brać się także z rozliczenia zamówienia kilkoma transakcjami albo z korekty w źródle. ' +
      'Zerowa różnica nie dowodzi, że zniżki nie było.',
  };

  const dowodPozycji: Dowod = {
    tytul: `Pozycje zamówienia — transakcja #${id}`,
    etykietaWartosci: 'Wiersze pozycji',
    wartosc: formatCount(data.order_items_count),
    podstawa: { volume: 'ORDER_ITEM_ROWS', time: 'ORDER_DATE' },
    zrodlo: `CRM /api/crm/transactions/${id} → crm_order_items złączone po numerze ZAMÓWIENIA`,
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: SWIEZOSC,
    oknoBiezace: oknoRekordu,
    oknoPorownawcze: null,
    wyliczenie:
      'Wiersze crm_order_items dla order_tuszama_id tej transakcji. Wartość linii = price_grosze × ' +
      'ilość, przy czym ilość NULL (system główny zapisuje pojedynczą sztukę jako brak ilości) ' +
      'liczy się do wartości jako 1 — ale w kolumnie „Ilość" zostaje pokazana jako brak.',
    szczegoly: [
      ['Zamówienie', data.order_id === null ? 'brak — transakcja bez zamówienia' : `#${data.order_id}`],
      ['Wiersze pozycji', formatCount(data.order_items_count)],
      ['Pozycje oznaczone jako skasowane', formatCount(usunietePozycje)],
      ['Pozycje bez ilości w źródle', formatCount(brakIlosci)],
      ['Suma wartości pozycji', sumaPozycji === null ? 'nie do policzenia' : formatGrosze(sumaPozycji)],
    ],
    proba: `${formatCount(data.order_items_count)} wierszy pozycji`,
    ograniczenia: [
      'Kwota pobrana NIE ISTNIEJE na ziarnie pozycji i nie jest rozdzielana proporcjonalnie — bez ' +
        'zaakceptowanego modelu alokacji byłaby liczbą zmyśloną wyglądającą na zmierzoną.',
      'Pozycje pobierane są po numerze ZAMÓWIENIA, więc przy zamówieniu rozliczonym kilkoma ' +
        'transakcjami te same pozycje widać przy każdej z nich.',
    ],
    czegoNieWynika:
      'Liczba wierszy pozycji to NIE liczba porcji ani liczba zamówień — mylenie tych trzech było ' +
      'defektem starego panelu. Nie wynika z niej też, ile kupujący zapłacił za konkretne danie.',
  };

  return (
    <Box data-testid="transaction-detail">
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 'var(--o3)' }}>
        <Typography variant="h6">Transakcja #{id}</Typography>
        {/* Kod statusu zostaje kodem. Jedyne, co o nim wiadomo ze źródła, to że analityka CRM
            zalicza do sprzedaży wiersze ze statusem 1 — i tylko to mówimy. */}
        <Chip
          size="small"
          variant="outlined"
          data-cc-card-id="TRANSACTIONS-11"
          label={`status: ${String(data.status ?? 'brak')}`}
          title="Kod statusu ze źródła. W analityce CRM za sprzedaż uznaje się wiersze ze statusem 1; znaczenia pozostałych kodów źródło nie dokumentuje."
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" onClick={onZamknij}>
          Zamknij
        </Button>
      </Stack>

      {/* ── DWIE KWOTY ─────────────────────────────────────────────────────────────────────
          Osobne bloki, różny materiał, jawny znak nierówności między nimi. */}
      <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)', display: 'block' }}>
        Dwie kwoty, dwie różne metryki
      </Typography>
      <Typography variant="body2" sx={{ color: 'var(--tekst-2)', mb: 'var(--o3)', maxWidth: '76ch' }}>
        Wartość wg cennika ≠ faktycznie pobrano. To nie są dwie wersje tej samej liczby: pierwsza
        opisuje ofertę przed zniżką szkolną, druga pieniądze, które wpłynęły.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gap: 'var(--o3)',
          gridTemplateColumns: { xs: '1fr', md: '1fr 48px 1fr' },
          alignItems: 'stretch',
          mb: 'var(--o3)',
        }}
      >
        <BlokKwoty
          kartaId="TRANSACTIONS-12"
          etykieta="Wartość wg cennika"
          grosze={wartoscKatalogowa}
          znaczenie="Ceny katalogowe pozycji, przed zniżką szkolną. To nie jest kwota, która wpłynęła."
          chipy={<MetricBasisChips basis={{ value: 'LIST_VALUE', time: 'ORDER_DATE' }} />}
          dowod={dowodWartosciKatalogowej}
        />

        <Box
          aria-hidden
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--krój-display)',
            fontSize: 32,
            color: 'var(--zloto)',
          }}
        >
          ≠
        </Box>

        <BlokKwoty
          ciemny
          kartaId="TRANSACTIONS-13"
          etykieta="Faktycznie pobrano"
          grosze={kwotaPobrana}
          znaczenie={'Pieniądze pobrane od kupującego. Brak wartości znaczy „nie wiemy", a nie „zero".'}
          chipy={<MetricBasisChips basis={{ value: 'PAID_AMOUNT', time: 'ORDER_DATE' }} />}
          dowod={dowodKwotyPobranej}
        />
      </Box>

      {/* ── RÓŻNICA ────────────────────────────────────────────────────────────────────────
          Osobny blok, bo różnica jest WNIOSKIEM z dwóch kwot, a nie trzecią kwotą. */}
      <Paper
        variant="outlined"
        data-cc-card-id="TRANSACTIONS-14"
        sx={{
          p: 'var(--o4)',
          mb: 'var(--o3)',
          borderRadius: 'var(--r-karta)',
          background: 'var(--powierzchnia-2)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--o4)',
          alignItems: 'center',
        }}
      >
        <Box sx={{ minWidth: 200 }}>
          <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)', display: 'block' }}>
            Różnica
          </Typography>
          <Typography
            className="sygnal-liczba"
            sx={{
              fontFamily: 'var(--krój-display)',
              fontSize: 'clamp(24px, 3vw, 32px)',
              lineHeight: 1.1,
              color: roznica === null ? 'var(--tekst-slaby)' : 'var(--tekst)',
              fontStyle: roznica === null ? 'italic' : 'normal',
            }}
          >
            {roznica === null ? 'nie do policzenia' : formatGrosze(roznica)}
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ color: 'var(--tekst-2)', flex: '1 1 32ch', maxWidth: '64ch' }}>
          {roznica === null
            ? 'Różnicy nie liczymy, dopóki nie znamy OBU kwot. Przy nieznanej kwocie pobranej wyszłaby ' +
              'równa pełnej wartości katalogowej i wyglądała jak stuprocentowy rabat.'
            : 'Wartość wg cennika minus faktycznie pobrano. To różnica dwóch metryk, nie „udzielona zniżka".'}
        </Typography>

        <Box sx={{ ml: '-10px' }}>
          <PrzyciskDowodu dowod={dowodRoznicy} etykieta="Skąd ta różnica" />
        </Box>
      </Paper>

      {data.missing_fields.length > 0 ? (
        <Alert severity="warning" data-cc-card-id="TRANSACTIONS-15" sx={{ mb: 'var(--o3)' }}>
          Źródło nie niesie pól: {data.missing_fields.join(', ')}. Brak nie jest zerem — ta transakcja
          po prostu nie ma tej wartości w rejestrze.
        </Alert>
      ) : null}

      {/* ── KONTEKST PŁATNOŚCI ─────────────────────────────────────────────────────────── */}
      <Paper
        variant="outlined"
        data-cc-card-id="TRANSACTIONS-16"
        sx={{ p: 'var(--o4)', mb: 'var(--o3)', borderRadius: 'var(--r-karta)' }}
      >
        <Box
          sx={{
            display: 'grid',
            gap: 'var(--o4)',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          }}
        >
          <Pole etykieta="Zamówienie" wartosc={data.order_id === null ? '—' : `#${data.order_id}`} />
          <Pole etykieta="Dzień zakupu" wartosc={data.order_date ?? '—'} podpis="oś czasu tego ekranu" />
          <Pole etykieta="Dzień dostawy" wartosc={data.fulfillment_date ?? '—'} />
          {/* Nazwa pola i podpis mówią wprost, że to szkoła Z CHWILI ZAKUPU. Kartoteka klienta
              ma kolumnę o TEJ SAMEJ NAZWIE, która niesie bieżącą szkołę ucznia. */}
          <Pole
            etykieta="Szkoła (historyczna)"
            wartosc={
              data.historical_school_name ??
              (data.historical_school_id === null ? '—' : `#${data.historical_school_id}`)
            }
            podpis="stan z dnia zakupu, nie bieżąca szkoła ucznia"
          />
          <Pole etykieta="Restauracja" wartosc={data.restaurant_name ?? '—'} />
          <Pole
            etykieta="Typ transakcji"
            wartosc={opisTypu(data.transaction_type)}
            podpis="kod ze źródła; nazwy wg definicji sprzedaży CRM"
          />
          <Pole
            etykieta="Kupujący (identyfikator)"
            wartosc={data.buyer_identifier_safe ?? '—'}
            podpis="bez danych osobowych — do reklamacji wystarcza identyfikator"
          />
        </Box>
      </Paper>

      {/* ── POZYCJE ────────────────────────────────────────────────────────────────────── */}
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 'var(--o2)', flexWrap: 'wrap' }}>
        <Typography variant="h6" data-cc-card-id="TRANSACTIONS-17">
          Pozycje ({formatCount(data.order_items_count)})
        </Typography>
        <MetricBasisChips basis={{ volume: 'ORDER_ITEM_ROWS' }} />
        <Box sx={{ ml: '-10px' }}>
          <PrzyciskDowodu dowod={dowodPozycji} etykieta="Skąd te pozycje" />
        </Box>
      </Stack>

      {data.order_items.length === 0 ? (
        <EmptyState
          kartaId="TRANSACTIONS-21"
          title="Brak pozycji"
          description={
            data.order_id === null
              ? 'Ta transakcja nie ma powiązanego zamówienia (order_id jest pusty), więc nie ma pozycji do pokazania.'
              : `Zamówienie #${data.order_id} nie ma pozycji w rejestrze CRM. To stan zasilania rejestru, a nie dowód, że zamówienie było puste.`
          }
        />
      ) : (
        <>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Pozycje zamówienia" data-cc-card-id="TRANSACTIONS-18">
              <TableHead>
                <TableRow>
                  <TableCell>Danie</TableCell>
                  <TableCell align="right">Ilość</TableCell>
                  <TableCell align="right">Cena katalogowa</TableCell>
                  <TableCell align="right">Wartość pozycji</TableCell>
                  {/* Kolumna istnieje i jest jawnie pusta. Ukrycie jej sugerowałoby, że kwota
                      pobrana po prostu nie została tu wpisana — a ona na tym ziarnie NIE ISTNIEJE. */}
                  <TableCell align="right" sx={{ borderLeft: '1px solid var(--linia)' }}>
                    Faktycznie pobrano
                  </TableCell>
                  <TableCell>Uwagi</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.order_items.map((it) => (
                  <TableRow key={it.order_item_id} hover>
                    <TableCell>
                      {it.dish_name ?? (it.dish_id === null ? '—' : `#${it.dish_id}`)}
                      {/* Katalog mógł zmienić nazwę po zakupie — przy reklamacji liczy się to,
                          co widział klient. */}
                      {it.dish_name_snapshot && it.dish_name_snapshot !== it.dish_name ? (
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--tekst-slaby)' }}>
                          w chwili zakupu: {it.dish_name_snapshot}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right" className="sygnal-liczba">
                      {it.quantity ?? (
                        <Typography component="span" variant="body2" sx={{ color: 'var(--tekst-slaby)', fontStyle: 'italic' }}>
                          brak
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Kwota grosze={it.historical_list_price_grosze} />
                    </TableCell>
                    <TableCell align="right">
                      <Kwota grosze={it.line_list_value_grosze} />
                    </TableCell>
                    <TableCell align="right" sx={{ borderLeft: '1px solid var(--linia)' }}>
                      <Typography component="span" variant="body2" sx={{ color: 'var(--tekst-slaby)', fontStyle: 'italic' }}>
                        brak na tym ziarnie
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {it.is_deleted ? (
                        <Chip size="small" color="warning" variant="outlined" label="pozycja skasowana" />
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow data-cc-card-id="TRANSACTIONS-19">
                  <TableCell colSpan={3} sx={{ color: 'var(--tekst-slaby)' }}>
                    Suma wartości pozycji (całe zamówienie)
                  </TableCell>
                  <TableCell align="right">
                    <Kwota grosze={sumaPozycji} brakLabel="nie do policzenia" />
                  </TableCell>
                  <TableCell align="right" sx={{ borderLeft: '1px solid var(--linia)' }}>
                    <Typography component="span" variant="body2" sx={{ color: 'var(--tekst-slaby)', fontStyle: 'italic' }}>
                      brak na tym ziarnie
                    </Typography>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>

          {/* Rozjazd sumy pozycji z wartością transakcji NIE jest sam w sobie błędem — i nie wolno
              go tak nazwać. Nazywamy fakt i podajemy, skąd bierze się różnica ziarna. */}
          {zgodnaSuma === false ? (
            <Alert severity="info" data-cc-card-id="TRANSACTIONS-20" sx={{ mt: 'var(--o2)' }}>
              Suma wartości pozycji ({formatGrosze(sumaPozycji)}) różni się od wartości wg cennika tej
              transakcji ({formatGrosze(wartoscKatalogowa)}). Pozycje pobierane są po numerze
              ZAMÓWIENIA i obejmują całe zamówienie, a transakcja może rozliczać jego część.
            </Alert>
          ) : null}
          {zgodnaSuma === null ? (
            <Typography
              variant="caption"
              data-cc-card-id="TRANSACTIONS-20"
              sx={{ color: 'var(--tekst-slaby)', display: 'block', mt: 'var(--o2)' }}
            >
              Sumy pozycji nie da się porównać z wartością transakcji: brakuje wartości co najmniej
              jednej pozycji albo wartości samej transakcji.
            </Typography>
          ) : null}
        </>
      )}

      <Alert severity="info" sx={{ mt: 'var(--o3)' }}>
        <Stack component="ul" sx={{ m: 0, pl: 2 }} spacing={0.25}>
          {data.caveats.map((c) => (
            <Typography key={c} component="li" variant="body2">
              {c}
            </Typography>
          ))}
        </Stack>
      </Alert>
    </Box>
  );
}

export function TransactionsPage() {
  const [szukanyId, setSzukanyId] = useState('');
  const [otwarta, setOtwarta] = useState<number | null>(null);

  /*
   * Logika pobierania danych zostaje bez zmian: pusty filtr = cały rejestr, poprawny numer =
   * pojedynczy wiersz. Ekran świadomie NIE zawęża po dacie — API to potrafi, ten widok tego
   * nie wysyła, więc mówimy o tym wprost zamiast sugerować okno, którego nie ma.
   */
  const zapytanie =
    szukanyId.trim() && /^\d+$/.test(szukanyId.trim()) ? { transaction_id: Number(szukanyId.trim()) } : {};
  const { data, isLoading, error } = useTransactions(zapytanie);

  const filtrAktywny = 'transaction_id' in zapytanie;
  const wpisanyTekst = szukanyId.trim().length > 0;
  const filtrNiepoprawny = wpisanyTekst && !filtrAktywny;
  const opisFiltru = filtrAktywny
    ? `numer transakcji = ${szukanyId.trim()}`
    : 'cały rejestr — bez zawężenia po dacie';

  /*
   * ZAWĘŻENIA PO STRONIE EKRANU. Wszystkie trzy czytają pola, które wiersz już niesie
   * (`status`, `missing_fields`, `paid_amount_grosze`) — żadne nie zgaduje i żadne nie wysyła
   * nowego zapytania. Konsekwencja jest nazwana na ekranie: filtrują POBRANĄ listę.
   */
  const [statusFiltr, setStatusFiltr] = useState<string>(WSZYSTKIE);
  const [tylkoNiekompletne, setTylkoNiekompletne] = useState(false);
  const [tylkoBezKwoty, setTylkoBezKwoty] = useState(false);

  /* Statusy bierzemy Z DANYCH, nie z listy wpisanej w kod: rejestr jest cudzy i jego zbiór
     wartości może się zmienić bez naszej wiedzy. Pigułka statusu, którego nikt nie ma,
     obiecywałaby wiersze, których nie ma. */
  const statusyWLiscie = data
    ? [...new Set(data.transactions.map((t) => t.status).filter((s): s is string => typeof s === 'string' && s !== ''))].sort()
    : [];

  const zaweznieAktywne = statusFiltr !== WSZYSTKIE || tylkoNiekompletne || tylkoBezKwoty;

  const wierszeWidoczne = (data?.transactions ?? []).filter(
    (t) =>
      (statusFiltr === WSZYSTKIE || t.status === statusFiltr) &&
      (!tylkoNiekompletne || t.missing_fields.length > 0) &&
      (!tylkoBezKwoty || t.paid_amount_grosze === null),
  );

  const pokazanych = data ? data.transactions.length : null;
  const pasujacych = data ? data.total : null;

  /*
   * ZERO TO NIE TO SAMO CO PUSTO. Reguła i jej uzasadnienie mieszkają w lib/pustyZbior.ts, razem
   * z testami, bo to niezmiennik całego produktu, a nie szczegół tego ekranu. Tutaj: brak wierszy
   * w rejestrze nie odróżnia „nikt nic nie kupił" od „rejestr nie jest zasilony", więc liczby-
   * -bohatera nie podajemy — podajemy brak i nazywamy jego rodzaj.
   */
  const ziarno = { wierszy: pokazanych, bytow: pasujacych };
  const zrodloPuste = czyPustyZbior(ziarno);
  const liczbaDoPokazania = wartoscDoPokazania(pasujacych, ziarno);

  /*
   * Liczone na WIERSZACH, które realnie przyszły — nie na całym rejestrze. Lista bywa ucięta
   * limitem, więc nazwanie tego udziałem w rejestrze byłoby nadużyciem.
   *
   * Przy pustym zbiorze podział „z kwotą / bez kwoty" nie istnieje — 0 z 0 nie jest wynikiem,
   * tylko brakiem czego dzielić. Dlatego przechodzi przez tę samą bramkę co liczba-bohater.
   */
  const zKwotaPobrana = data ? data.transactions.filter((t) => t.paid_amount_grosze !== null).length : null;
  const bezKwotyPobranej = data && pokazanych !== null ? pokazanych - (zKwotaPobrana ?? 0) : null;
  const zKwotaDoPokazania = wartoscDoPokazania(zKwotaPobrana, ziarno);
  const bezKwotyDoPokazania = wartoscDoPokazania(bezKwotyPobranej, ziarno);

  const dowodRejestru: Dowod = {
    tytul: 'Płatności w rejestrze',
    etykietaWartosci: 'Wiersze transakcji pasujące do filtra',
    wartosc: liczbaDoPokazania === null ? null : formatCount(liczbaDoPokazania),
    // Żadna z czterech jednostek wolumenu (zamówienia / porcje / kupujący / wiersze pozycji) nie
    // opisuje transakcji — podanie którejkolwiek byłoby dokładnie tym defektem starego panelu,
    // który liczył jedno i nazywał drugim. Dlatego wymiar wolumenu zostaje NIEPODANY.
    podstawa: { time: 'ORDER_DATE' },
    zrodlo: ZRODLO_LISTA,
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: SWIEZOSC,
    oknoBiezace: opisFiltru,
    oknoPorownawcze: null,
    wyliczenie:
      'count(*) po crm_transactions z tym samym warunkiem WHERE co lista, liczone PRZED limitem. ' +
      'Sama lista jest dodatkowo ucięta limitem odpowiedzi i posortowana malejąco po dacie zakupu.',
    szczegoly: [
      ['Filtr', opisFiltru],
      ['Pokazanych wierszy', formatCount(pokazanych)],
      ['Limit odpowiedzi', formatCount(data?.limit ?? null)],
      ['Lista ucięta', data ? (data.truncated ? 'tak' : 'nie') : '—'],
      ['Z kwotą pobraną (na liście)', formatCount(zKwotaDoPokazania)],
      ['Bez kwoty pobranej (na liście)', formatCount(bezKwotyDoPokazania)],
    ],
    proba: `${formatCount(pokazanych)} z ${formatCount(pasujacych)} wierszy`,
    ograniczenia: [
      ...(zrodloPuste
        ? [
            'ŹRÓDŁO ZWRÓCIŁO PUSTY ZBIÓR: ani jednego wiersza transakcji. To NIE jest zmierzone zero ' +
              'sprzedaży — płatności mogły być, ale nie ma ich w tym rejestrze.',
          ]
        : []),
      `Rejestr NIE stosuje filtra sprzedaży z analityki (${DEFINICJA_SPRZEDAZY}) — są tu również ` +
        'doładowania portfela i wiersze o innym statusie.',
      'Ekran nie zawęża po dacie: API przyjmuje date_from/date_to, ale ten widok ich nie wysyła.',
      'Udziały „z kwotą pobraną" liczone są na wierszach pokazanych, nie na całym rejestrze.',
    ],
    czegoNieWynika:
      'To NIE jest liczba zamówień ani liczba klientów: jedno zamówienie może być rozliczone więcej ' +
      'niż jedną transakcją (dlatego pozycje pobiera się po numerze zamówienia), a jeden kupujący ma ' +
      'wiele transakcji. Nie wynika z tego również, ile pieniędzy wpłynęło — rejestr liczy wiersze, ' +
      'nie kwoty.',
  };

  return (
    <Box>
      <NaglowekEkranu
        tytul="Transakcje"
        opis="Ekran odpowiada na jedno pytanie: co dokładnie zawierała ta płatność i ile naprawdę za nią pobrano. Szkoła pochodzi z transakcji (stan z dnia zakupu), nie z bieżącej szkoły ucznia. Danych osobowych kupującego tu nie ma — do odnalezienia płatności wystarcza identyfikator."
        akcja={
          <TextField
            size="small"
            data-cc-card-id="TRANSACTIONS-23"
            label="Numer transakcji"
            value={szukanyId}
            onChange={(e) => setSzukanyId(e.target.value)}
            error={filtrNiepoprawny}
            helperText={filtrNiepoprawny ? 'Numer transakcji to liczba — filtr nie został wysłany.' : ' '}
            sx={{ width: 220 }}
          />
        }
      />

      {/* Podstawa metryki stoi PRZY liczbach, nie w przypisie. Rejestr niesie DWIE podstawy kwoty
          i to jest najważniejsza rzecz, jaką ten ekran ma do powiedzenia. */}
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ flexWrap: 'wrap', alignItems: 'center', mb: 'var(--o4)' }}
      >
        <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)' }}>
          Dwie podstawy kwoty:
        </Typography>
        <MetricBasisChips basis={{ value: 'LIST_VALUE' }} />
        <Typography aria-hidden sx={{ color: 'var(--zloto)', fontWeight: 800 }}>
          ≠
        </Typography>
        <MetricBasisChips basis={{ value: 'PAID_AMOUNT', time: 'ORDER_DATE' }} />
      </Stack>

      {/*
        ══════════════════════════════════════════════════════════════════════════════════════
        PASEK FILTRÓW — `.pasek-filtrow` z makiety
        ══════════════════════════════════════════════════════════════════════════════════════

        Makieta ma tu pięć zawężeń; produkt miał wyłącznie wyszukiwanie po numerze. Odtwarzamy
        te, które da się zbudować z pól, które wiersz NAPRAWDĘ niesie.

        JEDNA RZECZ, KTÓREJ MAKIETA NIE MUSI ROZSTRZYGAĆ, A PRODUKT MUSI:
        makieta ma cały zbiór w pamięci, więc jej filtr zawęża REJESTR. Tutaj lista bywa UCIĘTA
        limitem serwera, więc filtr działający po stronie ekranu zawęża TO, CO PRZYSZŁO — a nie
        rejestr. Różnica jest widoczna w skutkach: „2 płatności bez kwoty pobranej" znaczy co
        innego przy pełnej liście i co innego przy pierwszych 200 z 4000 wierszy. Pasek mówi
        więc wprost, na czym operuje, zamiast pokazywać liczbę, która wygląda na wynik rejestru.
      */}
      {!isLoading && !error && data && data.transactions.length > 0 ? (
        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          sx={{ flexWrap: 'wrap', alignItems: 'center', mb: 'var(--o4)' }}
          role="group"
          aria-label="Zawężenia listy transakcji"
        >
          <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)' }}>
            Status
          </Typography>
          <Segmenty
            etykieta="Status transakcji"
            wybrana={statusFiltr}
            onZmiana={(w) => setStatusFiltr(w)}
            opcje={[
              { wartosc: WSZYSTKIE, etykieta: 'Wszystkie' },
              ...statusyWLiscie.map((s) => ({ wartosc: s, etykieta: s })),
            ]}
          />

          <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)' }}>
            Kompletność
          </Typography>
          <Chip
            size="small"
            variant={tylkoNiekompletne ? 'filled' : 'outlined'}
            color={tylkoNiekompletne ? 'warning' : 'default'}
            clickable
            onClick={() => setTylkoNiekompletne((v) => !v)}
            label="tylko niekompletne rekordy"
            title="Wiersze, którym rejestr nie podał kompletu pól — patrz kolumna Czego brakuje."
          />
          <Chip
            size="small"
            variant={tylkoBezKwoty ? 'filled' : 'outlined'}
            color={tylkoBezKwoty ? 'warning' : 'default'}
            clickable
            onClick={() => setTylkoBezKwoty((v) => !v)}
            label="tylko bez kwoty pobranej"
            title="Kwota pobrana nieznana. To NIE znaczy, że wyniosła zero."
          />

          <Box sx={{ flexGrow: 1 }} />

          <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', maxWidth: '52ch' }}>
            {zaweznieAktywne
              ? `Zawężenie działa na ${formatCount(data.transactions.length)} pobranych wierszach${data.truncated ? ` z ${formatCount(data.total)} pasujących` : ''}, nie na całym rejestrze.`
              : 'Bez zawężenia.'}
          </Typography>
        </Stack>
      ) : null}

      {isLoading ? <LoadingState label="Wczytywanie rejestru płatności…" /> : null}
      {error ? (
        <ErrorState
          error={error}
          context="Control Center czyta wyłącznie API. Gdy rejestr nie odpowiada, nie pokazujemy kwot zastępczych."
        />
      ) : null}

      {!isLoading && !error ? (
        <Stack spacing={3} className="sygnal-kaskada">
          {/*
           * PŁYTA GŁÓWNA tylko w widoku rejestru. W szczególe świadomie jej nie ma: musiałaby
           * wybrać jedną z dwóch równorzędnych kwot i tym samym podporządkować drugą.
           */}
          {otwarta === null && data ? (
            <PlytaGlowna
              kartaId="TRANSACTIONS-01"
              dowodId="TRANSACTIONS-06"
              etykieta="Płatności w rejestrze"
              wartosc={liczbaDoPokazania === null ? null : formatCount(liczbaDoPokazania)}
              opis={
                <Typography variant="body2">
                  {zrodloPuste
                    ? filtrAktywny
                      ? 'zapytanie o ten numer nie zwróciło żadnego wiersza — to nie dowód, że taka płatność nie istnieje'
                      : 'rejestr nie zwrócił ani jednego wiersza — to stan zasilania rejestru, nie zmierzone zero sprzedaży'
                    : 'wiersze transakcji — to nie jest liczba zamówień ani liczba kupujących'}
                </Typography>
              }
              zrodlo={ZRODLO_LISTA}
              okno={opisFiltru}
              dowod={dowodRejestru}
              boczne={
                <>
                  <MiniMetryka
                    kartaId="TRANSACTIONS-02"
                    wartosc={formatCount(pokazanych)}
                    etykieta="pokazanych"
                  />
                  <MiniMetryka wartosc={formatCount(data.limit)} etykieta="limit odpowiedzi" />
                  <MiniMetryka
                    kartaId="TRANSACTIONS-04"
                    wartosc={zKwotaDoPokazania === null ? null : formatCount(zKwotaDoPokazania)}
                    etykieta="z kwotą pobraną (na liście)"
                  />
                  <MiniMetryka
                    kartaId="TRANSACTIONS-05"
                    wartosc={bezKwotyDoPokazania === null ? null : formatCount(bezKwotyDoPokazania)}
                    etykieta="bez kwoty pobranej (na liście)"
                  />
                </>
              }
            />
          ) : null}

          {otwarta !== null ? <Szczegol id={otwarta} onZamknij={() => setOtwarta(null)} /> : null}

          {data && data.transactions.length === 0 ? (
            filtrAktywny ? (
              <EmptyState
                kartaId="TRANSACTIONS-09"
                title={`Nie znaleziono płatności o numerze ${szukanyId.trim()}`}
                description="Rejestr nie zwrócił wiersza o tym numerze. To nie dowodzi, że taka płatność nie istnieje — zapytanie z filtrem nie mówi, czy rejestr w ogóle jest zasilony."
              />
            ) : (
              <EmptyState
                kartaId="TRANSACTIONS-09"
                title="Brak transakcji"
                description="Rejestr nie zwrócił ani jednego wiersza w tym oknie. Z tego NIE wynika, że płatności nie było — brak wierszy wygląda tak samo przy pustym rejestrze i przy niezasilonym. Rozstrzyga to ekran Jakość danych, nie ten."
              />
            )
          ) : null}

          {data && data.transactions.length > 0 ? (
            <>
              <PasSekcji
                tytul="Rejestr płatności"
                opis="Pojedyncze transakcje — to z nich powstaje każda kwota na pozostałych ekranach."
              />
              <Karta nadtytul="Jedna płatność = jeden wiersz" tytul="Transakcje">
              <Typography
                variant="body2"
                data-cc-card-id="TRANSACTIONS-07"
                sx={{ color: 'var(--tekst-slaby)', mb: 'var(--o2)' }}
              >
                {data.truncated
                  ? `${formatCount(data.count)} z ${formatCount(data.total)} — lista ucięta do ${formatCount(data.limit)}. Zawęź filtr.`
                  : `${formatCount(data.count)} ${data.count === 1 ? 'płatność' : 'płatności'}.`}
                {zaweznieAktywne
                  ? ` Po zawężeniu widocznych: ${formatCount(wierszeWidoczne.length)}.`
                  : ''}
              </Typography>

              {/*
                ZAWĘŻENIE, KTÓRE NIC NIE ZWRÓCIŁO, TO WYNIK FILTRA — nie stan rejestru.
                Bez tego komunikatu zostawała sama tabela z nagłówkami kolumn i zerem wierszy,
                co czyta się jak „rejestr pusty" i prowadzi do zupełnie innej diagnozy.
              */}
              {zaweznieAktywne && wierszeWidoczne.length === 0 ? (
                <EmptyState
                  title="Żadna z pobranych płatności nie pasuje do zawężenia"
                  description={
                    `To jest wynik filtra, nie stan rejestru: pobrano ${formatCount(data.transactions.length)} ` +
                    `${data.transactions.length === 1 ? 'wiersz' : 'wierszy'}` +
                    `${data.truncated ? ` (z ${formatCount(data.total)} pasujących — lista ucięta)` : ''}` +
                    ' i żaden nie spełnia wybranych warunków. Zdejmij zawężenie, żeby zobaczyć całą pobraną listę.'
                  }
                />
              ) : null}

              <TableContainer sx={{ overflowX: 'auto', display: zaweznieAktywne && wierszeWidoczne.length === 0 ? 'none' : undefined }}>
                <Table size="small" aria-label="Rejestr transakcji" data-cc-card-id="TRANSACTIONS-08">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nr</TableCell>
                      <TableCell>Dzień zakupu</TableCell>
                      <TableCell>Szkoła (historyczna)</TableCell>
                      <TableCell>Restauracja</TableCell>
                      <TableCell align="right">Wartość wg cennika</TableCell>
                      {/* Kwota pobrana dostaje własną, wyraźnie oddzieloną kolumnę — także tutaj
                          nie stoi „obok" wartości katalogowej jak jej druga wersja. */}
                      <TableCell align="right" sx={{ borderLeft: '1px solid var(--linia-mocna)' }}>
                        Faktycznie pobrano
                      </TableCell>
                      <TableCell>Status (kod)</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {wierszeWidoczne.map((t: TransactionListRow) => (
                      <TableRow key={t.transaction_id} hover selected={t.transaction_id === otwarta}>
                        <TableCell className="sygnal-liczba">{t.transaction_id}</TableCell>
                        <TableCell>{t.order_date ?? '—'}</TableCell>
                        <TableCell>
                          {t.historical_school_name ??
                            (t.historical_school_id === null ? '—' : `#${t.historical_school_id}`)}
                        </TableCell>
                        <TableCell>{t.restaurant_name ?? '—'}</TableCell>
                        <TableCell align="right">
                          <Kwota grosze={t.list_value_total_grosze} />
                        </TableCell>
                        <TableCell align="right" sx={{ borderLeft: '1px solid var(--linia-mocna)' }}>
                          <Kwota grosze={t.paid_amount_grosze} />
                        </TableCell>
                        {/* KOD ŹRÓDŁA, nie etykieta. Rejestr podaje liczbę; słownik jej znaczeń
                            nie jest po naszej stronie potwierdzony, więc nie podstawiamy nazw.
                            Zgadnięta etykieta („opłacona", „anulowana") czytałaby się jak
                            ustalenie, a byłaby domysłem. */}
                        <TableCell title="Kod statusu z rejestru CRM — bez tłumaczenia na nazwę">
                          {String(t.status ?? '—')}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            onClick={() => setOtwarta(t.transaction_id)}
                            aria-label={`Otwórz transakcję ${t.transaction_id}`}
                          >
                            Otwórz
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block', mt: 'var(--o2)' }}>
                Kolumna „Faktycznie pobrano" bywa pusta — źródło nie niesie tej wartości dla części
                płatności. Pusto znaczy „nie wiemy", nigdy „0,00 zł".
              </Typography>
            </Karta>
            </>
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
}
